import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Header, Footer } from '@/components/public';
import { SEOHead } from '@/components/seo';
import BlogContent from '@/components/public/BlogContent';
import { GuideFaq } from '@/components/public/GuideFaq';
import { getMarketForSlug, SITE_ORIGIN } from '@/components/public/marketGuides';
import '@/components/public/uk/uk.css';

const CATEGORY_LABELS = {
  formation: 'Business Formation',
  compliance: 'Compliance',
  tax: 'Tax & Finance',
  guides: 'Guides',
  news: 'News & Updates',
};

// Reusable premium renderer for the market-focused blog articles. Shares the
// country-page green theme (uk.css / .uk-* classes) so every article matches the guides.
export default function MarketGuidePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const market = getMarketForSlug(slug);
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [contents, setContents] = useState([]);
  const articleRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const controller = new AbortController();
    setError('');
    setPost(null);
    setRelated([]);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/blog/${slug}`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error(response.status === 404 ? 'This article is not currently published.' : 'The article could not be loaded.');
        return response.json();
      })
      .then(setPost)
      .catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/blog/${slug}/related?limit=4`, { signal: controller.signal })
      .then(r => (r.ok ? r.json() : { posts: [] }))
      .then(d => setRelated(d.posts || []))
      .catch(() => {});
    return () => controller.abort();
  }, [slug, retry]);

  // Build the "In this guide" TOC from the rendered article DOM so it works for
  // both HTML posts (h2[id]) and Markdown posts (ids added via rehype-slug).
  useEffect(() => {
    if (!post) { setContents([]); return; }
    const t = setTimeout(() => {
      const root = articleRef.current;
      if (!root) return;
      const items = [...root.querySelectorAll('h2[id]')].map((el) => ({ id: el.id, title: el.textContent }));
      setContents(items);
    }, 0);
    return () => clearTimeout(t);
  }, [post]);

  useEffect(() => {
    if (post && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [post, location.hash]);

  const image = post?.featured_image || market?.image;

  return <div className="uk-page" data-testid="market-guide-page">
    <SEOHead
      title={post?.meta_title || 'StartEase Agent'}
      description={post?.meta_description || 'An original StartEase guide to global company formation.'}
      canonical={`${SITE_ORIGIN}/blog/${slug}`}
      ogImage={post?.og_image_url || post?.featured_image || market?.image}
      ogType="article"
      author={post?.author}
      keywords={post ? [post.target_keyword, ...(post.secondary_keywords || []), ...(post.tags || [])].filter(Boolean).join(', ') : undefined}
    >
      {post && <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'Article', headline: post.title, description: post.meta_description, datePublished: post.published_at, dateModified: post.updated_at, image: post.og_image_url, author: { '@type': 'Organization', name: post.author }, mainEntityOfPage: `${SITE_ORIGIN}/blog/${slug}` })}</script>}
      {post && <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN }, { '@type': 'ListItem', position: 2, name: 'Founder Guides', item: `${SITE_ORIGIN}/blog` }, { '@type': 'ListItem', position: 3, name: post.title, item: `${SITE_ORIGIN}/blog/${slug}` }] })}</script>}
    </SEOHead>
    <Header />
    <main>
      {!post ? (
        <div className="uk-wrap uk-load-state" data-testid="market-guide-load-state">
          {error ? <>
            <p role="alert" data-testid="market-guide-error">{error}</p>
            <button className="uk-button" onClick={() => setRetry(retry + 1)} data-testid="market-guide-retry">Try again</button>
          </> : <p role="status" data-testid="market-guide-loading"><Loader2 className="animate-spin" />Loading the article…</p>}
          <Link to="/blog" className="uk-text-link block" data-testid="market-guide-loading-back">Back to Insights</Link>
        </div>
      ) : <>
        <header className="uk-guide-header"><div className="uk-wrap">
          <nav className="uk-breadcrumb" aria-label="Breadcrumb" data-testid="market-guide-breadcrumb">
            {market && <><Link to={market.path} data-testid="market-guide-back-country">{market.label}</Link><span>/</span></>}
            <Link to="/blog" data-testid="market-guide-blog-link">Insights</Link><span>/</span><span>Guide</span>
          </nav>
          <span className="uk-eyebrow">StartEase perspectives{market && <><span className="uk-sep" />{market.label}</>}</span>
          <h1 data-testid="market-guide-title">{post.title}</h1>
          <p data-testid="market-guide-excerpt">{post.excerpt}</p>
          <div className="uk-guide-meta">
            <span data-testid="market-guide-author">{post.author}</span>
            <span data-testid="market-guide-date">{new Date(post.updated_at || post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span data-testid="market-guide-reading-time">{post.reading_time_minutes} min read</span>
          </div>
        </div></header>
        <div className="uk-wrap uk-article-layout">
          <nav className="uk-article-toc" aria-label="In this guide" data-testid="market-guide-toc">
            {contents.length > 0 && <span className="uk-eyebrow">In this guide</span>}
            {contents.map(({ id, title }) => <a key={id} href={`#${id}`} data-testid={`market-guide-toc-${id}`}>{title}</a>)}
            {market && <Link to={market.path} data-testid="market-guide-toc-back"><ArrowLeft size={14} className="inline mr-2" />{market.label} services</Link>}
          </nav>
          <article className="uk-article-body" data-testid="market-guide-article" ref={articleRef}>
            {image && <img className="uk-article-cover" src={image} alt={market?.alt || post.title} data-testid="market-guide-cover" />}
            <BlogContent content={post.content} />
            <GuideFaq faqs={post.faqs} />
            <div className="uk-article-cta" data-testid="market-guide-closing">
              <p>A clear brief is a good place to start.</p>
              <Link to={market ? market.path : '/blog'} className="uk-button" data-testid="market-guide-enquire">Request a consultation<ArrowRight size={16} /></Link>
            </div>
            {related.length > 0 && (
              <section className="uk-related" data-testid="related-guides-section">
                <span className="uk-eyebrow">Related guides</span>
                <div className="uk-related-grid">
                  {related.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/blog/${r.slug}`}
                      className="uk-related-card"
                      data-testid={`related-guide-link-${r.slug}`}
                    >
                      <span className="uk-related-cat">{CATEGORY_LABELS[r.category] || 'Guide'}</span>
                      <span className="uk-related-title">{r.title}<ArrowRight size={15} className="uk-related-arrow" /></span>
                      {r.reading_time_minutes ? <span className="uk-related-meta">{r.reading_time_minutes} min read</span> : null}
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </>}
    </main>
    <Footer />
  </div>;
}
